import { AnimatePresence } from 'framer-motion';
import Image from 'next/image';
import React, { FC, useCallback, useEffect, useState } from 'react';

import RenderIf from '@/components/Common/RenderIf';
import ShortVideoModel from '@/components/HomePage/ShortVideo/modal';
import HorizontalScroll from '@/components/Layout/Header/Search/Suggestions/HorizontalScroll';

import { Endpoints } from '@/network';
import { generateUrlForImage } from '@/pages/books/[bookId]';
import { IShowcaseShortVideo } from '@/utils/interfaces/ShowcaseShortVideo';

interface Props {
  showcaseShortVideos: IShowcaseShortVideo[];
}

/** Build the current URL with a bytes param added or removed */
const buildBytesUrl = (bytesId?: string) => {
  const url = new URL(window.location.href);
  if (bytesId) {
    url.searchParams.set('bytes', bytesId);
  } else {
    url.searchParams.delete('bytes');
  }
  return url.pathname + url.search;
};

const ShortVideo: FC<Props> = ({ showcaseShortVideos }) => {
  const [shortVideoModal, setShortVideoModal] = useState<{
    show: boolean;
    activeIndex: number;
  }>({ show: false, activeIndex: 0 });

  // On mount, check if ?bytes= is already in the URL and open the modal
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const bytesId = params.get('bytes');
    if (bytesId) {
      const index = showcaseShortVideos.findIndex((v) => v._id === bytesId);
      if (index >= 0) {
        setShortVideoModal({ show: true, activeIndex: index });
      }
    }
  }, [showcaseShortVideos]);

  // Listen for browser back/forward to close/open modal
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const bytesId = params.get('bytes');
      if (bytesId) {
        const index = showcaseShortVideos.findIndex((v) => v._id === bytesId);
        if (index >= 0) {
          setShortVideoModal({ show: true, activeIndex: index });
        } else {
          setShortVideoModal({ show: false, activeIndex: 0 });
        }
      } else {
        setShortVideoModal({ show: false, activeIndex: 0 });
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [showcaseShortVideos]);

  const openShort = useCallback(
    (index: number) => {
      const shortId = showcaseShortVideos[index]?._id;
      if (!shortId) return;
      // Push a new history entry so browser back closes the modal
      window.history.pushState(null, '', buildBytesUrl(shortId));
      setShortVideoModal({ show: true, activeIndex: index });
    },
    [showcaseShortVideos],
  );

  const closeShort = useCallback(() => {
    // Remove the bytes param from the URL
    window.history.pushState(null, '', buildBytesUrl());
    setShortVideoModal({ show: false, activeIndex: 0 });
  }, []);

  const getEmbedUrl = (id: string) => {
    return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&controls=0&modestbranding=1&rel=0&playsinline=1`;
  };

  return (
    <>
      <div className='w-full mx-auto '>
        <HorizontalScroll
          classNameRoot=''
          arrowVarient='vertical'
          buttonClass={{
            left: 'sm:w-8 sm:h-8 w-6 h-6 rounded-full bg-black/40 backdrop-blur-[8px] flex items-center justify-center',
            right:
              'sm:w-8 sm:h-8 w-6 h-6 rounded-full bg-black/40 backdrop-blur-[8px] flex items-center justify-center',
            leftIcon: 'text-white sm:w-6 w-4 sm:h-6 h-4',
            rightIcon: 'text-white sm:w-6 w-4 sm:h-6 h-4',
          }}
          scrollAmount={250}
          className='sm:gap-3 gap-2 px-4 md:px-5 lg:px-6 xl:px-12 py-10'
        >
          <AnimatePresence mode='popLayout'>
            {showcaseShortVideos.map((showcaseShortVideo, index) => (
              <div
                data-ph-capture-attribute-event-type='bytes_popup_opened'
                data-ph-capture-attribute-product-name={
                  showcaseShortVideo?.bookId?.title || 'Book'
                }
                onClick={() => {
                  openShort(index);
                  window.posthog?.capture('bytes_popup_opened', {
                    product_name: showcaseShortVideo?.bookId?.title || 'Book',
                  });
                }}
                key={showcaseShortVideo._id}
                className='bg-grayZl-950 rounded-lg overflow-hidden 
              2xl:w-[246px] 2xl:min-w-[246px] 
              xl:w-[240px] xl:min-w-[240px] 
              lg:w-[200px] lg:min-w-[200px] 
              md:w-[160px] md:min-w-[160px] 
              w-[140px] min-w-[140px]'
              >
                <div className='relative w-full aspect-[9/16] overflow-hidden'>
                  <iframe
                    src={getEmbedUrl(showcaseShortVideo.youtubeId)}
                    className='absolute inset-0 w-full h-full'
                    allow='autoplay; encrypted-media'
                    allowFullScreen
                  />
                  <div className='w-full h-full bg-transparent top-0 left-0 absolute '></div>
                </div>

                <div
                  // onClick={() =>
                  //   router.push(
                  //     `/books/${showcaseShortVideo?.bookId?.slug}-${showcaseShortVideo?.bookId?._id}`,
                  //   )
                  // }
                  className='flex flex-col items-center gap-3 relative z-20 cursor-pointer'
                >
                  <Image
                    className='md:w-[74px] xs400:w-[58px] w-[49px] h-auto rounded-sm -mt-8'
                    width={74}
                    height={104}
                    alt='Book'
                    src={generateUrlForImage(
                      `${process.env.NEXT_PUBLIC_API_READER_URL}/`,
                      `${Endpoints.getCoverImagesForBooks}/`,
                      `${showcaseShortVideo.bookId?._id}`,
                    )}
                  />
                  <p className='text-white font-semibold lg:text-lg/[22px] md:text-base/[20px] xs400:text-sm/[17px] text-xs xl:mb-[26px] xs400:mb-5 mb-4 mx-2 text-center line-clamp-2'>
                    {showcaseShortVideo.bookId.title}
                  </p>
                </div>
              </div>
            ))}
          </AnimatePresence>
        </HorizontalScroll>
      </div>
      <RenderIf condition={shortVideoModal.show}>
        <ShortVideoModel
          key={shortVideoModal.activeIndex}
          initialSlide={shortVideoModal.activeIndex}
          onClose={closeShort}
          showcaseShortVideos={showcaseShortVideos}
        />
      </RenderIf>
    </>
  );
};

export default ShortVideo;
